import * as Yup from 'yup';
import { useFormik } from 'formik';
import {
  Button,
  Text,
  GridFlex,
  Box,
  DetailsLayout,
  drawFormFields,
  FadeBox,
  Link,
  Field,
  FormRenderProps,
} from '@stigg-components';
import { FeatureFlags } from '@stigg-types/featureFlags';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { t } from 'i18next';
import { useSelector } from 'react-redux';
import styled from 'styled-components/macro';
import { PageCard } from '../../../../components/PageCard';
import { RootState, useAppDispatch } from '../../../../redux/store';
import { rolesValidationSchema, setRolesFields } from './setRolesFields';
import { updateAccountAction } from '../../accountsSlice';
import { TooltipFields } from '../../../../components/InformationTooltip';
import { getAccessMethodField, getAccessMethodFieldValidationSchema } from './AccessMethodField';
import { getAuthorizedDomainField } from './AuthorizedDomainField';
import { useAuth0 } from '@auth0/auth0-react';
import { Trans } from 'react-i18next';
import { useNavigation } from '../../../../modules/navigation/useNavigation';
import { SettingsPageTabs } from '../../types/settingsPageTabs';
import { FORBIDDEN_EMAIL_DOMAINS } from './forbiddenEmailDomains.const';
import { useBooleanEntitlement } from '../../../../doggo/hooks/useBooleanEntitlement';
import { StiggFeature } from '../../../../doggo/StiggFeature';
import { LoginOptionsForm } from './LoginOptions.types';
import { getDefaultFormValues, getUserDomain } from './LoginOptions.utils';
import { useAccountVerifiedDomains } from '../../queries/fetchAccountVerifiedDomains';
import { AccountAccessMethod, AccountAccessRole } from '@stigg-types/apiTypes';
import { useCurrentMember } from '../../../auth/hooks/useCurrentMember';
import { SsoContactUs } from './SsoContactUs';

export const NotConfiguredStyledBox = styled(Box)`
  display: flex;
  align-items: center;
  width: 100%;
  padding: ${({ theme }) => theme.spacing(4)};
  border-radius: ${({ theme }) => theme.itamar.border.radius};
  color: ${({ theme }) => theme.itamar.palette.text.primary};
  background-color: ${({ theme }) => theme.itamar.palette.background.emptyState};
`;

const defaultAccessSection = (rolesFields: Field<LoginOptionsForm>[], form: FormRenderProps<LoginOptionsForm>) => (
  <>
    <Text.B2 $bold my={4}>
      {t('accounts.sso.defaultRolesTitle')}
    </Text.B2>

    <GridFlex.Column rowGap={4} $fullWidth>
      <GridFlex.RowCenter $fullWidth>
        <GridFlex.Item sx={{ width: 200, minWidth: 200, mr: 2 }}>
          <Text.B2 color="secondary">{t('accounts.loginOptions.accountRole.fieldLabel')}</Text.B2>
        </GridFlex.Item>
        <GridFlex.Item sx={{ width: 250 }}>{drawFormFields([rolesFields[0]], form)}</GridFlex.Item>
      </GridFlex.RowCenter>
      <GridFlex.RowCenter>
        <GridFlex.Item sx={{ width: 200, minWidth: 200, mr: 2 }}>
          <Text.B2 color="secondary">{t('accounts.loginOptions.productionAccess.fieldLabel')}</Text.B2>
        </GridFlex.Item>
        <GridFlex.Item sx={{ width: 250 }}>{drawFormFields([rolesFields[1]], form)}</GridFlex.Item>
      </GridFlex.RowCenter>
      <GridFlex.RowCenter>
        <GridFlex.Item sx={{ width: 200, minWidth: 200, mr: 2 }}>
          <Text.B2 color="secondary">{t('accounts.loginOptions.nonProductionAccess.fieldLabel')}</Text.B2>
        </GridFlex.Item>
        <GridFlex.Item sx={{ width: 250 }}>{drawFormFields([rolesFields[2]], form)}</GridFlex.Item>
      </GridFlex.RowCenter>
    </GridFlex.Column>
  </>
);

function LoginOptions() {
  const { rbacRollout } = useFlags<FeatureFlags>();
  const navigation = useNavigation();
  const { hasAccess: hasRbacAccess } = useBooleanEntitlement(StiggFeature.RBAC);
  const { hasAccess: hasAuthorizedDomainAccess } = useBooleanEntitlement(StiggFeature.AUTHORIZED_DOMAIN);
  const { hasAccess: hasSsoAccess } = useBooleanEntitlement(StiggFeature.SSO_SAML);
  const { data: accountVerifiedDomains } = useAccountVerifiedDomains();
  const currentMember = useCurrentMember();

  const isOwner = currentMember?.accessRoles?.accountRole === AccountAccessRole.Owner;
  const account = useSelector((state: RootState) => state.accountReducer.account);
  const { user } = useAuth0();
  const userDomain = getUserDomain(user);
  const dispatch = useAppDispatch();
  const isSsoConfigured = account?.samlEnabled || false;
  const isRbacEnabled = rbacRollout && hasRbacAccess;
  const isAccountRoleEnabled = isRbacEnabled && isOwner;

  const approvedDomains = accountVerifiedDomains?.filter((domain) => !FORBIDDEN_EMAIL_DOMAINS.includes(domain)) || [];

  const accessDeniedTooltip: TooltipFields | undefined = {
    placement: 'top',
    title: <Text.B2 color="primary">{t('permissions.changeAccessMethodDenied')}</Text.B2>,
  };

  const noDomainErrorTooltip: TooltipFields | undefined = {
    placement: 'bottom',
    title: <Text.B2 color="primary">{t('accounts.loginOptions.authorizedDomain.noDomainErrorTooltip')}</Text.B2>,
  };

  const getValidationSchema = () => {
    return Yup.lazy(() => {
      if (form.values.accessMethod === AccountAccessMethod.Sso) {
        return Yup.object().shape({
          ...getAccessMethodFieldValidationSchema(),
          ssoRoles: Yup.object().shape(rolesValidationSchema).required(t('fieldValidationMessages.required')),
        });
      }
      if (form.values.accessMethod === AccountAccessMethod.AuthorizedDomain) {
        return Yup.object().shape({
          ...getAccessMethodFieldValidationSchema(),
          authorizedDomain: Yup.string()
            .notOneOf(FORBIDDEN_EMAIL_DOMAINS)
            .required(t('fieldValidationMessages.required')),
          authorizedDomainRoles: Yup.object()
            .shape(rolesValidationSchema)
            .required(t('fieldValidationMessages.required')),
        });
      }
      if (form.values.accessMethod === AccountAccessMethod.InviteOnly) {
        return Yup.object().shape({
          ...getAccessMethodFieldValidationSchema(),
        });
      }

      throw new Error('unknown access method');
    });
  };

  const submitForm = async (values: LoginOptionsForm) => {
    if (!account) return;
    const { accessMethod } = values;

    if (accessMethod === AccountAccessMethod.Sso) {
      await dispatch(
        updateAccountAction({
          displayName: account.displayName,
          accessMethod,
          defaultSSORoles: {
            accountRole: values.ssoRoles?.accountRole,
            nonProductionRole: values.ssoRoles?.nonProductionAccess,
            productionRole: values.ssoRoles?.productionAccess,
          },
        }),
      );
    } else if (accessMethod === AccountAccessMethod.AuthorizedDomain) {
      await dispatch(
        updateAccountAction({
          displayName: account.displayName,
          accessMethod,
          accountEmailDomain: values.authorizedDomain,
          defaultSSORoles: {
            accountRole: values.authorizedDomainRoles?.accountRole,
            nonProductionRole: values.authorizedDomainRoles?.nonProductionAccess,
            productionRole: values.authorizedDomainRoles?.productionAccess,
          },
        }),
      );
    } else if (accessMethod === AccountAccessMethod.InviteOnly) {
      await dispatch(
        updateAccountAction({
          displayName: account.displayName,
          accessMethod,
        }),
      );
    } else throw new Error('unknown access method');
  };

  const form = useFormik<LoginOptionsForm>({
    enableReinitialize: true,
    initialValues: getDefaultFormValues(isRbacEnabled, account, userDomain),
    validationSchema: getValidationSchema(),
    onSubmit: submitForm,
  });

  const getRbacDisableText = () => {
    const disableTooltip: TooltipFields | undefined = {
      placement: 'top',
      title: (
        <Text.B2 color="primary">
          <Trans
            i18nKey="accounts.rbacUpgradePlan"
            components={[
              <Link
                onClick={() =>
                  navigation.navigateTo(navigation.appRoutes.settingsPage({ selectedTab: SettingsPageTabs.Billing }))
                }
              />,
            ]}
          />
        </Text.B2>
      ),
    };

    return disableTooltip;
  };

  const getToolTips: () => { accountTooltip?: TooltipFields; environmentTooltip?: TooltipFields } = () => {
    if (isRbacEnabled) {
      return {
        accountTooltip: !isAccountRoleEnabled ? accessDeniedTooltip : undefined,
        environmentTooltip: !isAccountRoleEnabled ? accessDeniedTooltip : undefined,
      };
    }

    const disableText = getRbacDisableText();

    return {
      accountTooltip: disableText,
      environmentTooltip: disableText,
    };
  };

  const { accountTooltip, environmentTooltip } = getToolTips();

  const commonSetRolesOptions: Parameters<typeof setRolesFields>[0] = {
    hide: false,
    withLabels: false,
    accountRoleDisabled: !isAccountRoleEnabled,
    environmentRoleDisabled: !isAccountRoleEnabled,
    accountTooltip,
    environmentTooltip,
  };
  const ssoRolesFields = setRolesFields<LoginOptionsForm>({
    ...commonSetRolesOptions,
    idPrefix: 'ssoRoles',
  });

  const authorizedDomainRolesFields = setRolesFields<LoginOptionsForm>({
    ...commonSetRolesOptions,
    idPrefix: 'authorizedDomainRoles',
  });

  const accessMethodField = getAccessMethodField<LoginOptionsForm>({
    disabledMap: {
      SSO: !hasSsoAccess,
      AUTHORIZED_DOMAIN: !hasAuthorizedDomainAccess,
      INVITE_ONLY: false,
    },
    disabled: !isOwner,
    tooltip: !isOwner ? accessDeniedTooltip : undefined,
  });

  const authorizedDomainTooltip: TooltipFields | undefined = !isAccountRoleEnabled
    ? accessDeniedTooltip
    : approvedDomains.length === 0
      ? noDomainErrorTooltip
      : undefined;

  const authorizedDomainField = getAuthorizedDomainField<LoginOptionsForm>({
    domains: approvedDomains,
    disabled: !isAccountRoleEnabled || approvedDomains.length === 0,
    tooltip: authorizedDomainTooltip,
  });

  const authorizedDomainSection = (
    <GridFlex.RowCenter $fullWidth>
      <GridFlex.Item sx={{ width: 200, minWidth: 200, mr: 2 }}>
        <Text.B2 color="secondary">{t('accounts.loginOptions.authorizedDomain.fieldLabel')}</Text.B2>
      </GridFlex.Item>
      <GridFlex.Item sx={{ width: 250 }}>{drawFormFields([authorizedDomainField], form)}</GridFlex.Item>
    </GridFlex.RowCenter>
  );
  const shouldDisableSave =
    !form.isValid ||
    (form.values.accessMethod === AccountAccessMethod.Sso && !isSsoConfigured) ||
    (form.values.accessMethod === AccountAccessMethod.AuthorizedDomain &&
      (!form.values.authorizedDomain || approvedDomains.length === 0));
  return (
    <PageCard>
      <GridFlex.Column $fullWidth rowGap={4}>
        <GridFlex.Row alignItems="start" $fullWidth justifyContent="space-between">
          <GridFlex.Column rowGap={2}>
            <Text.H3 mb={2}>{t('accounts.loginOptions.title')}</Text.H3>
            <GridFlex.Row>
              <Text.Sub2 color="secondary" mr={1}>
                {t('accounts.loginOptions.subtitle')}
              </Text.Sub2>
            </GridFlex.Row>
            <GridFlex.RowCenter $fullWidth marginTop={4}>
              <GridFlex.Item sx={{ width: 200, minWidth: 200, mr: 2 }}>
                <Text.B2 color="secondary">{t('accounts.loginOptions.accessMethod.fieldLabel')}</Text.B2>
              </GridFlex.Item>
              <GridFlex.Item sx={{ width: 250 }}>{drawFormFields([accessMethodField], form)}</GridFlex.Item>
            </GridFlex.RowCenter>
          </GridFlex.Column>

          <FadeBox className={!form.dirty ? 'hidden' : ''}>
            <GridFlex.Row>
              <Button onClick={() => form.resetForm()} sx={{ mr: 3 }} $outlined color="primary">
                {t('sharedComponents.cancel')}
              </Button>
              <Button variant="contained" color="primary" disabled={shouldDisableSave} onClick={form.submitForm}>
                {t('sharedComponents.saveChanges')}
              </Button>
            </GridFlex.Row>
          </FadeBox>
        </GridFlex.Row>

        {form.values.accessMethod === AccountAccessMethod.AuthorizedDomain ? (
          <>
            {authorizedDomainSection}
            <GridFlex.Column>{defaultAccessSection(authorizedDomainRolesFields, form)}</GridFlex.Column>
          </>
        ) : null}

        {form.values.accessMethod !== 'SSO' ? null : isSsoConfigured ? (
          <GridFlex.Column gap={6}>
            <DetailsLayout
              titleWidth={200}
              rowGap={4}
              details={[
                {
                  title: t('accounts.sso.idp'),
                  content: t('accounts.sso.idpValue'),
                },
                {
                  title: t('accounts.sso.authDomain'),
                  content: account?.accountEmailDomain,
                  helperTooltip: <Text.B2>{t('accounts.sso.authDomainTooltip')}</Text.B2>,
                },
              ]}
            />
            <GridFlex.Column marginTop={4}>{defaultAccessSection(ssoRolesFields, form)}</GridFlex.Column>
          </GridFlex.Column>
        ) : null}
      </GridFlex.Column>

      <GridFlex.RowCenter $fullWidth marginTop={4}>
        <GridFlex.Item sx={{ width: 200, minWidth: 200, mr: 2 }}></GridFlex.Item>
        {form.values.accessMethod === 'SSO' && !isSsoConfigured ? (
          <GridFlex.Item sx={{ width: 400, minWidth: 400, mr: 2 }}>{SsoContactUs()}</GridFlex.Item>
        ) : null}
      </GridFlex.RowCenter>
    </PageCard>
  );
}

export default LoginOptions;
